import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { api, ApiError } from '../lib/api';
import { agentStatusChip } from '../lib/agentStatus';
import type { Agent, AgentStatus } from '@shared/types';

export default function Agents() {
  const nav = useNavigate();
  const [agents, setAgents] = useState<Agent[] | null>(null); // null = loading
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api.get<Agent[]>('/agents');
      setAgents(rows);
      setErr('');
    } catch (e) {
      setErr(e instanceof ApiError ? `HTTP ${e.status}` : 'Could not reach the office.');
      // keep any rows we already have so a poll blip doesn't blank the grid
      setAgents((prev) => prev);
    }
  }, []);

  // living roster — poll so orb tempo stays honest
  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  async function toggleKill(a: Agent) {
    const on = !a.kill_switch; // on:true = pause, on:false = resume
    // Pausing cancels queued work, so confirm the one destructive direction
    // (the detail page already does — this closes the accidental-tap footgun).
    if (
      on &&
      !window.confirm(
        `Pause ${a.name}? Any tasks they haven't started yet will be cancelled, and ${a.name} will stop working until you resume them.`,
      )
    ) {
      return;
    }
    setBusy(a.id);
    try {
      await api.post('/admin/kill', { agent_id: a.id, on });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? `HTTP ${e.status}` : `Couldn't reach ${a.name} — nothing changed.`);
    } finally {
      setBusy(null);
    }
  }

  // --- states ---
  if (agents === null && !err) {
    return (
      <Layout title="Agents">
        <p className="small">Loading the team…</p>
      </Layout>
    );
  }

  if (agents === null) {
    return (
      <Layout title="Agents">
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
            <Orb agent="quinn" size="md" state="blocked" />
          </div>
          <p className="dim">The roster wouldn't load — {err}. Quinn's on it.</p>
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-line sm" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (agents.length === 0) {
    return (
      <Layout title="Agents">
        <div className="card" style={{ textAlign: 'center', padding: '56px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <Orb agent="emma" size="md" state="idle" />
          </div>
          <p className="dim">No teammates on the roster yet — the office is quiet.</p>
        </div>
      </Layout>
    );
  }

  const roster = [...agents].sort((x, y) => x.sort_order - y.sort_order);

  return (
    <Layout title="Agents">
      <div className="grid g4">
        {roster.map((a) => {
          const chip = agentStatusChip(a);
          const state: AgentStatus = a.kill_switch ? 'off' : a.status; // killed → orb sleeps
          return (
            <div
              key={a.id}
              className="card"
              role="button"
              tabIndex={0}
              onClick={() => nav(`/agents/${a.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  nav(`/agents/${a.id}`);
                }
              }}
              style={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                textAlign: 'center',
              }}
            >
              <Orb agent={a.id} size="md" state={state} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{a.name}</div>
                <div className="small" style={{ marginTop: 2 }}>
                  {a.role}
                </div>
              </div>
              <span className={`chip ${chip.cls}`} title={chip.tip}>
                <i />
                {chip.label}
              </span>
              <button
                className="btn btn-ghost sm"
                disabled={busy === a.id}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleKill(a);
                }}
              >
                {a.kill_switch ? 'Resume' : 'Pause'}
              </button>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
